import { FC, PropsWithChildren, ReactNode } from 'react';

import { useAtomValue } from 'jotai';

import {
    useActivity,
    useActivitySwings,
    useCameraConfig,
    useCameraSetup,
    useCorridors,
    useLayoutById,
    useLayoutCustomizations,
    useLayouts,
    useStudent,
    useUserSettings,
} from '@queries';
import { ErrorScreen, LoadingScreen } from '@screens';
import {
    activeUserIdAtom,
    activeUserSettingsAtom,
    activityAtom,
    activityIdAtom,
    activitySwingsAtom,
    activityUnitAtom,
    cameraConfigAtom,
    cameraSetupAtom,
    corridorsAtom,
    isActiveCurrentUserAtom,
    layoutAtom,
    useSyncAtom,
    withActivityScope,
} from '@store';

const ActivityGuard: FC<PropsWithChildren<{ errAction: ReactNode }>> = ({ children, errAction }) => {
    const activityId = useAtomValue(activityIdAtom);
    const activeUserId = useAtomValue(activeUserIdAtom);

    // We load this to determine whether this is a coach or the activity owner.
    // We can use this state to explicitly block users with a clear message, if they try to view activity they don't have access to.
    const isActiveCurrentUser = useAtomValue(isActiveCurrentUserAtom);
    const studentRequest = useStudent(activeUserId);

    const activityResponse = useActivity(activityId);
    useSyncAtom(activityAtom, activityResponse.data);
    const activitySwingsResponse = useActivitySwings(activityId);

    useSyncAtom(activitySwingsAtom, activitySwingsResponse.data);

    /**
     * User settings and their dependencies
     */
    const userSettingsRequest = useUserSettings(activeUserId);
    useSyncAtom(activeUserSettingsAtom, userSettingsRequest.data);
    const activityUnit = useAtomValue(activityUnitAtom);
    const layoutRequest = useLayoutById(activityResponse.data?.layoutId || -1);
    useSyncAtom(layoutAtom, layoutRequest.data);

    const cameraConfigRequest = useCameraConfig(activityId);
    useSyncAtom(cameraConfigAtom, cameraConfigRequest.data);

    const cameraSetupRequest = useCameraSetup(activityId);
    useSyncAtom(cameraSetupAtom, cameraSetupRequest.data);

    const corridorsRequest = useCorridors(activityUnit);
    useSyncAtom(corridorsAtom, corridorsRequest.data);

    if (activityResponse.isLoading) return <LoadingScreen message="Loading activity data..." />;
    if (activityId === -1) return <ErrorScreen message="No activity selected." action={errAction} />;
    if (activityResponse.isError) return <ErrorScreen message="You cannot access this page." action={errAction} />;

    if (activitySwingsResponse.isLoading) return <LoadingScreen message="Loading activity swings data..." />;
    if (activitySwingsResponse.isError)
        return <ErrorScreen message="You cannot access this page." action={errAction} />;

    if (userSettingsRequest.isError) return <ErrorScreen message="Failed to load user settings." action={errAction} />;
    if (userSettingsRequest.isLoading || userSettingsRequest.data?.userId !== activeUserId)
        return <LoadingScreen message="Loading user settings..." />;

    if (cameraSetupRequest.isLoading) return <LoadingScreen message="Loading camera setup..." />;
    if (cameraSetupRequest.isError) return <ErrorScreen message="You cannot access this page." action={errAction} />;

    if (layoutRequest.isLoading) return <LoadingScreen message="Loading activity layout..." />;
    if (layoutRequest.isError) return <ErrorScreen message="Failed to load activity layout." action={errAction} />;

    // Early return to avoid student request check, when user is not viewing student's activity.
    if (isActiveCurrentUser) return children;

    if (studentRequest.isLoading) return <LoadingScreen message="Loading student data..." />;
    if (studentRequest.isError) return <ErrorScreen message="You cannot access this page." action={errAction} />;

    return children;
};

export const ActivityLoader = withActivityScope(ActivityGuard);
